import React, { useEffect, useRef, useState } from 'react';
import { X, RefreshCw, CheckCircle, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

/**
 * Manual Entry Modal Component
 * Desktop keeps the existing single-form layout. Mobile uses a two-step wizard:
 * 1) customer/contact information
 * 2) vehicle information, with existing advanced fields available on demand
 */
export const ManualEntryModal = ({
  show,
  onClose,
  formData,
  setFormData,
  onSubmit,
  vinDecoding,
  vinData,
  onDecodeVIN,
  theme,
  colors
}) => {
  const modalContentRef = useRef(null);
  const formRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [mobileStep, setMobileStep] = useState(1);
  const [showAdditional, setShowAdditional] = useState(false);

  useEffect(() => {
    if (show) {
      setMobileStep(1);
      setShowAdditional(false);
    }
  }, [show]);

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const resolvedValue = type === 'checkbox'
      ? checked
      : name === 'vin'
        ? value.toUpperCase()
        : value;

    setFormData({
      ...formData,
      [name]: resolvedValue
    });
  };

  const goToVehicleStep = () => {
    if (formRef.current && !formRef.current.reportValidity()) return;
    setMobileStep(2);
    modalContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToContactStep = () => {
    setMobileStep(1);
    modalContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const inputStyle = {
    width: '100%',
    padding: '16px 20px',
    borderRadius: '14px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    background: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(10px)',
    color: '#fff',
    fontSize: '15px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
    boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const sectionHeading = {
    gridColumn: '1 / -1',
    fontSize: '14px',
    fontWeight: '600',
    color: colors.primary,
    marginTop: '12px',
    marginBottom: '8px',
    paddingBottom: '8px',
    borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
  };

  const labelStyle = {
    display: 'block',
    color: theme.textSecondary,
    fontSize: '13px',
    marginBottom: '6px'
  };

  return (
    <div className="manual-modal-overlay" style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      animation: 'fadeIn 0.3s ease-out'
    }}>
      <div
        ref={modalContentRef}
        className="manual-modal-content"
        style={{
          position: 'relative',
          background: 'linear-gradient(135deg, rgba(255,255,255,.1) 0%, rgba(255,255,255,.05) 20%, rgba(255,255,255,.02) 50%, rgba(255,255,255,.05) 80%, rgba(255,255,255,.1) 100%)',
          backdropFilter: 'blur(40px) saturate(150%)',
          WebkitBackdropFilter: 'blur(40px) saturate(150%)',
          border: '1px solid rgba(255,255,255,.15)',
          borderRadius: '24px',
          boxShadow: '0 20px 60px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.2)',
          padding: '40px',
          width: '90%',
          maxWidth: '700px',
          maxHeight: '90vh',
          overflowY: 'auto',
          animation: 'scaleIn 0.4s cubic-bezier(0.4,0,0.2,1)'
        }}
      >
        <style>{`
          .manual-mobile-progress,
          .manual-mobile-actions,
          .manual-additional-toggle { display: none; }

          @media (max-width: 768px) {
            .manual-modal-overlay { align-items: flex-end !important; }
            .manual-modal-content {
              width: 100% !important;
              max-width: 100% !important;
              max-height: 94dvh !important;
              border-radius: 24px 24px 0 0 !important;
              padding: 22px 18px calc(22px + env(safe-area-inset-bottom)) !important;
            }
            .manual-modal-header {
              margin-bottom: 16px !important;
              padding-bottom: 16px !important;
            }
            .manual-modal-header h2 { font-size: 25px !important; }
            .manual-mobile-progress { display: block; margin-bottom: 18px; }
            .manual-progress-label {
              display: flex;
              justify-content: space-between;
              color: rgba(255,255,255,.62);
              font-size: 12px;
              margin-bottom: 8px;
            }
            .manual-progress-track {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 7px;
            }
            .manual-progress-segment {
              height: 4px;
              border-radius: 999px;
              background: rgba(255,255,255,.10);
              overflow: hidden;
            }
            .manual-progress-segment.active {
              background: linear-gradient(90deg, #3b82f6, #60a5fa);
              box-shadow: 0 0 14px rgba(59,130,246,.45);
            }
            .manual-grid {
              display: block !important;
              max-height: none !important;
              overflow: visible !important;
              padding: 0 !important;
            }
            .manual-step { display: none; }
            .manual-step.active { display: grid; grid-template-columns: 1fr; gap: 14px; }
            .manual-step h4 { margin-top: 0 !important; }
            .manual-vin-row { display: grid !important; grid-template-columns: 1fr !important; gap: 9px !important; }
            .manual-vin-row button { width: 100%; min-height: 46px; }
            .manual-mobile-actions {
              display: flex;
              gap: 10px;
              position: sticky;
              bottom: 0;
              margin: 18px -18px -22px;
              padding: 14px 18px calc(14px + env(safe-area-inset-bottom));
              background: linear-gradient(180deg, rgba(8,12,24,.70), rgba(8,12,24,.98) 28%);
              backdrop-filter: blur(18px);
              z-index: 5;
            }
            .manual-mobile-actions button { flex: 1; min-height: 48px; }
            .manual-desktop-submit { display: none !important; }
            .manual-additional-toggle {
              display: flex;
              width: 100%;
              align-items: center;
              justify-content: space-between;
              margin-top: 4px;
              padding: 14px 16px;
              border-radius: 12px;
              border: 1px solid rgba(59,130,246,.20);
              background: rgba(59,130,246,.07);
              color: #dbeafe;
              font-size: 13px;
              font-weight: 600;
              cursor: pointer;
            }
            .manual-additional-fields { display: none; }
            .manual-additional-fields.open {
              display: grid;
              grid-template-columns: 1fr;
              gap: 14px;
              margin-top: 4px;
            }
          }

          @media (min-width: 769px) {
            .manual-step { display: contents; }
            .manual-additional-fields { display: contents !important; }
          }
        `}</style>

        <div className="manual-modal-header" style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
          borderBottom: '1px solid rgba(255,255,255,.1)',
          paddingBottom: '24px'
        }}>
          <h2 style={{ color: '#fff', margin: 0, fontSize: '32px', fontWeight: '600' }}>Add Customer</h2>
          <button type="button" onClick={onClose} aria-label="Close" style={{
            width: '40px', height: '40px', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)', borderRadius: '12px', color: 'rgba(255,255,255,.7)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}><X size={20} /></button>
        </div>

        <div className="manual-mobile-progress">
          <div className="manual-progress-label">
            <span>{mobileStep === 1 ? 'Contact Info' : 'Vehicle Info'}</span>
            <span>Step {mobileStep} of 2</span>
          </div>
          <div className="manual-progress-track">
            <div className="manual-progress-segment active" />
            <div className={`manual-progress-segment ${mobileStep === 2 ? 'active' : ''}`} />
          </div>
        </div>

        <form ref={formRef} onSubmit={handleSubmit} className="manual-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '16px',
          padding: '4px'
        }}>
          <section className={`manual-step ${mobileStep === 1 ? 'active' : ''}`}>
            <h4 style={sectionHeading}>Customer Information</h4>

            <div><label style={labelStyle}>First Name *</label><input type="text" name="firstName" value={formData.firstName || ''} onChange={handleChange} required style={inputStyle} /></div>
            <div><label style={labelStyle}>Last Name *</label><input type="text" name="lastName" value={formData.lastName || ''} onChange={handleChange} required style={inputStyle} /></div>
            <div><label style={labelStyle}>Phone *</label><input type="tel" name="cPhone" value={formData.cPhone || ''} onChange={handleChange} required style={inputStyle} /></div>
            <div><label style={labelStyle}>Email</label><input type="email" name="email" value={formData.email || ''} onChange={handleChange} style={inputStyle} /></div>
            <div><label style={labelStyle}>Business</label><input type="text" name="business" value={formData.business || ''} onChange={handleChange} style={inputStyle} /></div>
            <div><label style={labelStyle}>Source</label><select name="source" value={formData.source || ''} onChange={handleChange} style={inputStyle}><option value="facebook">Facebook</option><option value="google">Google</option><option value="service-drive">Service Drive</option><option value="walk-in">Walk-In</option><option value="referral">Referral</option></select></div>
          </section>

          <section className={`manual-step ${mobileStep === 2 ? 'active' : ''}`}>
            <h4 style={sectionHeading}>Vehicle Information</h4>

            <div className="manual-vin-row" style={{ gridColumn: '1 / -1', display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}><label style={labelStyle}>VIN</label><input type="text" name="vin" value={formData.vin || ''} onChange={handleChange} maxLength={17} style={{ ...inputStyle, fontFamily: 'monospace', textTransform: 'uppercase' }} /></div>
              <button type="button" onClick={() => onDecodeVIN(formData.vin)} disabled={vinDecoding || !formData.vin || formData.vin.length !== 17} style={{
                padding: '12px 16px', borderRadius: '8px', border: 'none', background: vinDecoding || !formData.vin || formData.vin.length !== 17 ? 'rgba(59,130,246,.3)' : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`, color: '#fff', cursor: vinDecoding || !formData.vin || formData.vin.length !== 17 ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600'
              }}>{vinDecoding ? 'Decoding...' : 'Decode VIN'}</button>
            </div>

            <div><label style={labelStyle}>Year</label><input type="number" name="vehicleYear" value={formData.vehicleYear || ''} onChange={handleChange} style={inputStyle} /></div>
            <div><label style={labelStyle}>Make</label><input type="text" name="vehicleMake" value={formData.vehicleMake || ''} onChange={handleChange} style={inputStyle} /></div>
            <div><label style={labelStyle}>Model</label><input type="text" name="vehicleModel" value={formData.vehicleModel || ''} onChange={handleChange} style={inputStyle} /></div>
            <div><label style={labelStyle}>Trim</label><input type="text" name="vehicleTrim" value={formData.vehicleTrim || ''} onChange={handleChange} style={inputStyle} /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Mileage</label><input type="number" name="mileage" value={formData.mileage || ''} onChange={handleChange} style={inputStyle} /></div>

            <button type="button" className="manual-additional-toggle" onClick={() => setShowAdditional(v => !v)}>
              <span>Additional details</span>
              <ChevronDown size={17} style={{ transform: showAdditional ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s ease' }} />
            </button>

            <div className={`manual-additional-fields ${showAdditional ? 'open' : ''}`}>
              <h4 style={sectionHeading}>Financial Information</h4>
              <div><label style={labelStyle}>Payoff Amount</label><input type="number" step="0.01" name="payoffAmount" value={formData.payoffAmount || ''} onChange={handleChange} style={inputStyle} /></div>
              <div><label style={labelStyle}>Per Diem</label><input type="number" step="0.01" name="perDiem" value={formData.perDiem || ''} onChange={handleChange} style={inputStyle} /></div>
              <div><label style={labelStyle}>Good Until Date</label><input type="date" name="payoffGoodUntil" value={formData.payoffGoodUntil || ''} onChange={handleChange} style={inputStyle} /></div>
              <div><label style={labelStyle}>Lender Name</label><input type="text" name="lenderName" value={formData.lenderName || ''} onChange={handleChange} style={inputStyle} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Payoff Date</label><input type="date" name="payoffDate" value={formData.payoffDate || ''} onChange={handleChange} style={inputStyle} /></div>

              <h4 style={sectionHeading}>Lead Assignment</h4>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Assign To</label><select name="assignedTo" value={formData.assignedTo || ''} onChange={handleChange} style={inputStyle}><option value="">AI Auto-Assign (Recommended)</option><option value="Tyler Brodsky">Tyler Brodsky</option><option value="Mike Johnson">Mike Johnson</option><option value="Sarah Chen">Sarah Chen</option></select></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Priority</label><select name="priority" value={formData.priority || 'normal'} onChange={handleChange} style={inputStyle}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option></select></div>

              <h4 style={sectionHeading}>Appointment Scheduling (Optional)</h4>
              <div style={{ gridColumn: '1 / -1' }}><label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: theme.text, fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}><input type="checkbox" name="scheduleAppointment" checked={Boolean(formData.scheduleAppointment)} onChange={handleChange} style={{ width: '18px', height: '18px' }} />Schedule Appointment</label></div>
              {formData.scheduleAppointment && <>
                <div><label style={labelStyle}>Appointment Date</label><input type="date" name="appointmentDate" value={formData.appointmentDate || ''} onChange={handleChange} style={inputStyle} /></div>
                <div><label style={labelStyle}>Appointment Time</label><input type="time" name="appointmentTime" value={formData.appointmentTime || ''} onChange={handleChange} style={inputStyle} /></div>
                <div><label style={labelStyle}>Appraiser</label><select name="appointmentAppraiser" value={formData.appointmentAppraiser || ''} onChange={handleChange} style={inputStyle}><option value="Tyler Brodsky">Tyler Brodsky</option><option value="Mike Johnson">Mike Johnson</option><option value="Sarah Chen">Sarah Chen</option></select></div>
                <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Appointment Notes</label><textarea name="appointmentNotes" value={formData.appointmentNotes || ''} onChange={handleChange} rows={3} style={{ ...inputStyle, minHeight: '90px', resize: 'vertical' }} /></div>
              </>}

              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Notes</label><textarea name="notes" value={formData.notes || ''} onChange={handleChange} rows={3} style={{ ...inputStyle, minHeight: '90px', resize: 'vertical' }} /></div>
            </div>
          </section>

          <div className="manual-desktop-submit" style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
            <button type="submit" disabled={submitting} style={{
              padding: '14px 28px', background: 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)', color: '#fff', border: 'none', borderRadius: '12px', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', minWidth: '140px', justifyContent: 'center', opacity: submitting ? .7 : 1
            }}>{submitting ? <><RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />Adding...</> : <><CheckCircle size={18} />Add Lead</>}</button>
          </div>

          <div className="manual-mobile-actions">
            {mobileStep === 1 ? (
              <button type="button" onClick={goToVehicleStep} style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff', border: 'none', borderRadius: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>Next <ChevronRight size={18} /></button>
            ) : (
              <>
                <button type="button" onClick={goToContactStep} style={{ background: 'rgba(255,255,255,.06)', color: '#fff', border: '1px solid rgba(255,255,255,.12)', borderRadius: '12px', fontWeight: '650', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}><ChevronLeft size={18} />Back</button>
                <button type="submit" disabled={submitting} style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff', border: 'none', borderRadius: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', opacity: submitting ? .7 : 1 }}>{submitting ? <><RefreshCw size={17} style={{ animation: 'spin 1s linear infinite' }} />Adding...</> : <><CheckCircle size={17} />Add Customer</>}</button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
